import { useEffect, useRef, useState } from 'react';
import type { MistranslationReport } from '@cixing/shared';
import { submitReport } from '../api.js';
import { markReportSynced, saveReport } from '../db/idb.js';
import type { Segment } from '../state/session.js';
import { useSessionStore } from '../state/session.js';

/**
 * 誤訳の報告。
 *
 * 保存はまず IndexedDB へ行う。講義中に回線が切れていても記録を失わないため。
 * サーバー送信は best-effort で、失敗しても利用者の操作は完了させる。
 * 管理画面ではこの報告から用語集へ直接反映できる(取込と同じ経路を通る)。
 */
export function ReportDialog({
  segment,
  onClose,
}: {
  segment: Segment;
  onClose: () => void;
}): JSX.Element {
  const [suggested, setSuggested] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const sessionId = useSessionStore((s) => s.sessionId);
  const profiles = useSessionStore((s) => s.profiles);
  const glossaryVersion = useSessionStore((s) => s.glossaryVersion);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleSubmit = async (): Promise<void> => {
    setSaving(true);

    const report: MistranslationReport = {
      id: `${segment.segmentId}-${Date.now()}`,
      sessionId,
      segmentId: segment.segmentId,
      sourceZh: segment.sourceZh,
      translatedJa: segment.translatedJa,
      ...(suggested.trim() ? { suggestedJa: suggested.trim() } : {}),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      profiles,
      glossaryVersion,
      createdAt: new Date().toISOString(),
      synced: false,
    };

    // 端末に確実に残す。ここが失敗したときだけ利用者に知らせる。
    await saveReport(report);

    try {
      await submitReport(report);
      await markReportSynced(report.id);
    } catch {
      // 送信は後で再試行する。利用者には成功として見せてよい
      // (端末には保存済みで、エクスポートもできるため)。
    }

    setSaving(false);
    onClose();
  };

  return (
    <dialog ref={dialogRef} onClose={onClose}>
      <h2 style={{ fontSize: 18, marginTop: 0 }}>誤訳の報告</h2>

      <div className="field">
        <label>中国語原文</label>
        <div style={{ fontSize: 14, color: 'var(--text-dim)' }}>{segment.sourceZh}</div>
      </div>

      <div className="field">
        <label>現在の日本語訳</label>
        <div style={{ fontSize: 16 }}>{segment.translatedJa}</div>
      </div>

      <div className="field">
        <label>正しい日本語訳(分かる場合)</label>
        <input
          value={suggested}
          onChange={(e) => setSuggested(e.target.value)}
          placeholder="例: カム"
        />
      </div>

      <div className="field">
        <label>コメント(任意)</label>
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="例: 三角 は編み機ではカムのこと"
        />
      </div>

      <div className="row">
        <button onClick={onClose} disabled={saving}>
          キャンセル
        </button>
        <div className="spacer" />
        <button className="primary" onClick={() => void handleSubmit()} disabled={saving}>
          {saving ? '保存しています…' : '報告する'}
        </button>
      </div>
    </dialog>
  );
}
